/**
 * Calculated Account Change Propagation Utilities
 * 
 * This module handles the detection and calculation of changes that affect calculated accounts.
 * Similar to Master Stats, calculated accounts need to be recalculated when their reference
 * accounts are changed.
 */

import formula from 'excel-formula';
import { evaluate } from 'mathjs';
import { getMonthsBetweenDates } from './utils';
import { debugLogger } from './debugLogger';

/**
 * Builds a map of reference account GL keys to calculated accounts that use them
 * @param {Array} calculatedAccounts - Array of calculated account definitions
 * @returns {Object} Map of glKey -> array of calculated account IDs
 */
export const buildReferenceAccountMap = (calculatedAccounts) => {
  const refAccountMap = {};
  
  if (!calculatedAccounts || calculatedAccounts.length === 0) {
    return refAccountMap;
  }
  
  calculatedAccounts.forEach(calcAccount => {
    if (!calcAccount.reference_accounts || !Array.isArray(calcAccount.reference_accounts)) {
      return;
    }
    
    calcAccount.reference_accounts.forEach(refAccount => {
      const glKey = refAccount.glKey;
      if (!refAccountMap[glKey]) {
        refAccountMap[glKey] = [];
      }
      
      // Store calculated account info
      if (!refAccountMap[glKey].includes(calcAccount.id)) {
        refAccountMap[glKey].push(calcAccount.id);
      }
    });
  });
  
  return refAccountMap;
};

/**
 * Finds all calculated accounts affected by a change
 * @param {Object} change - The change being applied
 * @param {Array} calculatedAccounts - Array of calculated account definitions
 * @param {Object} refAccountMap - Map of reference accounts to calculated accounts (optional, will build if not provided)
 * @returns {Array} Array of calculated accounts affected by this change
 */
export const findAffectedCalculatedAccounts = (change, calculatedAccounts, refAccountMap = null) => {
  if (!change || !calculatedAccounts || calculatedAccounts.length === 0) {
    return [];
  }
  
  debugLogger.log('CALC_PROPAGATION', 'Finding affected calculated accounts', {
    changeLabel: change.change_label,
    selectedAccountsCount: change.selected_accounts?.length,
    calculatedAccountsCount: calculatedAccounts.length
  });
  
  // Build map if not provided
  const map = refAccountMap || buildReferenceAccountMap(calculatedAccounts);
  
  // Get all GL keys affected by this change
  const affectedGlKeys = change.selected_accounts || [];
  
  if (affectedGlKeys.length === 0) {
    return [];
  }
  
  // Find all calculated accounts that reference any of the affected GL keys
  const affectedCalcAccountIds = new Set();
  
  affectedGlKeys.forEach(glKey => {
    const calcAccountIds = map[glKey] || [];
    calcAccountIds.forEach(id => affectedCalcAccountIds.add(id));
  });
  
  // Return the full calculated account objects
  const affectedCalcAccounts = calculatedAccounts.filter(ca => 
    affectedCalcAccountIds.has(ca.id)
  );
  
  debugLogger.log('CALC_PROPAGATION', 'Found affected calculated accounts', {
    changeLabel: change.change_label,
    affectedCount: affectedCalcAccounts.length,
    affectedIds: affectedCalcAccounts.map(ca => ca.id)
  });
  
  return affectedCalcAccounts;
};

/**
 * Calculates the change impact on a calculated account for a specific month
 * @param {Object} calcAccount - Calculated account definition
 * @param {String} month - Month in 'MMM yyyy' format
 * @param {Object} referenceAccountData - Map of glKey -> account data with base values
 * @param {Object} referenceAccountChanges - Map of glKey -> change values for this month
 * @returns {Object} { baseValue, newValue, changeImpact }
 */
export const calculateMonthlyChangeImpact = (calcAccount, month, referenceAccountData, referenceAccountChanges) => {
  debugLogger.log('CALC_ACCOUNT', 'Calculating monthly change impact', {
    calcAccountLabel: calcAccount.change_label,
    month,
    formula: calcAccount.ref_account_formula,
    referenceAccountCount: calcAccount.reference_accounts?.length
  });
  
  try {
    // Calculate base value (without changes)
    let baseFormulaString = formula.toJavaScript(calcAccount.ref_account_formula);
    
    calcAccount.reference_accounts.forEach(refAccount => {
      const refData = referenceAccountData[refAccount.glKey];
      const baseValue = refData?.[month] || 0;
      baseFormulaString = baseFormulaString.replaceAll(refAccount.rowLetter, baseValue);
    });
    
    const baseValue = evaluate(baseFormulaString);
    
    // Calculate new value (with changes)
    let newFormulaString = formula.toJavaScript(calcAccount.ref_account_formula);
    
    calcAccount.reference_accounts.forEach(refAccount => {
      const refData = referenceAccountData[refAccount.glKey];
      const baseValue = refData?.[month] || 0;
      const changeValue = referenceAccountChanges[refAccount.glKey]?.[month] || 0;
      const totalValue = baseValue + changeValue;
      newFormulaString = newFormulaString.replaceAll(refAccount.rowLetter, totalValue);
    });
    
    const newValue = evaluate(newFormulaString);
    const changeImpact = newValue - baseValue;
    
    debugLogger.log('CALC_ACCOUNT', 'Monthly calculation complete', {
      calcAccountLabel: calcAccount.change_label,
      month,
      baseValue,
      newValue,
      changeImpact
    });
    
    return {
      baseValue,
      newValue,
      changeImpact
    };
  } catch (error) {
    debugLogger.error('CALC_ACCOUNT', 'Error calculating change impact', error);
    console.error(`Error calculating change impact for ${calcAccount.change_label} in ${month}:`, error);
    return {
      baseValue: 0,
      newValue: 0,
      changeImpact: 0
    };
  }
};

/**
 * Calculates all monthly change impacts for a calculated account affected by changes
 * @param {Object} calcAccount - Calculated account definition
 * @param {Array} affectingChanges - Array of changes that affect this calculated account
 * @param {Object} referenceAccountData - Map of glKey -> account data with base values
 * @param {Object} projectInfo - Project info with start/end dates
 * @returns {Object} Map of month -> change impact value
 */
export const calculateAllMonthlyImpacts = (calcAccount, affectingChanges, referenceAccountData, projectInfo) => {
  const monthlyImpacts = {};
  
  if (!projectInfo?.start_date || !projectInfo?.end_date) {
    return monthlyImpacts;
  }
  
  // Get all months in the project
  const allMonths = getMonthsBetweenDates(projectInfo.start_date, projectInfo.end_date);
  
  // Build a map of reference account changes by month
  const referenceAccountChanges = {};
  
  // Initialize all reference accounts
  calcAccount.reference_accounts.forEach(refAccount => {
    referenceAccountChanges[refAccount.glKey] = {};
  });
  
  // Aggregate all changes affecting each reference account
  affectingChanges.forEach(change => {
    const changeDates = getMonthsBetweenDates(change.date_range[0], change.date_range[1]);
    
    change.selected_accounts.forEach(glKey => {
      // Only process if this is one of our reference accounts
      if (referenceAccountChanges.hasOwnProperty(glKey)) {
        changeDates.forEach(month => {
          if (!referenceAccountChanges[glKey][month]) {
            referenceAccountChanges[glKey][month] = 0;
          }
          
          // Get the change value for this month
          const changeValue = getChangeValueForMonth(change, month, glKey, referenceAccountData[glKey]);
          referenceAccountChanges[glKey][month] += changeValue;
        });
      }
    });
  });
  
  // Calculate impact for each month
  allMonths.forEach(month => {
    const impact = calculateMonthlyChangeImpact(
      calcAccount,
      month,
      referenceAccountData,
      referenceAccountChanges
    );
    
    monthlyImpacts[month] = impact.changeImpact;
  });
  
  return monthlyImpacts;
};

/**
 * Gets the change value for a specific month from a change object
 * Handles different change types (percentage, fixed value, etc.)
 * @param {Object} change - The change object
 * @param {String} month - Month in 'MMM yyyy' format
 * @param {String} glKey - GL key of the account
 * @param {Object} accountData - Account data with base values
 * @returns {Number} Change value for this month
 */
const getChangeValueForMonth = (change, month, glKey, accountData) => {
  // Check if this account is affected by this change
  if (!change.selected_accounts || !change.selected_accounts.includes(glKey)) {
    return 0;
  }
  
  // Check if this month is within the change date range
  const changeDates = getMonthsBetweenDates(change.date_range[0], change.date_range[1]);
  if (!changeDates.includes(month)) {
    return 0;
  }
  
  // Get base value for this month
  const baseValue = accountData?.[month] || 0;
  
  // Parse operator value used for the change calculations
  const operatorValue = Number(change.operator_value);
  if (Number.isNaN(operatorValue)) {
    console.warn(`Invalid operator_value for change "${change.change_label}" (${change.id}):`, change.operator_value);
    return 0;
  }
  
  // Calculate change based on function operator
  switch (change.function_operator) {
    case 'percentage':
      return baseValue * (operatorValue / 100);
    
    case 'fixed':
      return operatorValue;
    
    case 'value':
      return operatorValue - baseValue;
    
    default:
      console.warn(`Unknown function operator: ${change.function_operator}`);
      return 0;
  }
};

/**
 * Logs information about calculated accounts affected by changes
 * @param {Array} changes - Array of changes being applied
 * @param {Array} calculatedAccounts - Array of calculated account definitions
 */
export const logAffectedCalculatedAccounts = (changes, calculatedAccounts) => {
  if (!changes || changes.length === 0 || !calculatedAccounts || calculatedAccounts.length === 0) {
    return;
  }
  
  const refAccountMap = buildReferenceAccountMap(calculatedAccounts);
  
  changes.forEach(change => {
    const affected = findAffectedCalculatedAccounts(change, calculatedAccounts, refAccountMap);
    
    if (affected.length > 0) {
      console.log(`\nChange "${change.change_label}" affects ${affected.length} calculated account(s):`);
      affected.forEach(ca => {
        console.log(`  - ${ca.change_label} (ID: ${ca.id})`);
        console.log(`    Reference accounts:`, ca.reference_accounts.map(ra => ra.glKey));
        console.log(`    Affected by GL keys:`, change.selected_accounts.filter(glKey => 
          ca.reference_accounts.some(ra => ra.glKey === glKey)
        ));
      });
    }
  });
  
  console.groupEnd();
};
